import { Form, Input, Select, Switch, Alert, Space, Typography } from 'antd';
import type {
  SsoConfig,
  SsoProvider,
  RoleToTypeRule,
  ExistingUserRolePolicy,
  ExistingUserLocationPolicy,
} from './types';
import { MASKED_SECRET } from './types';
import { RoleMappingRulesEditor } from './RoleMappingRulesEditor';
import { PolicyAndDefaultSelectors } from './PolicyAndDefaultSelectors';
import type { FieldErrors } from './validation';

/**
 * Editable presentation form for an SSO configuration (SF-3180, Tasks 1–4).
 *
 * Tasks 1–3 shipped this as view-only (AntD Form `disabled`), with only
 * the role-mapping rule editor and the policy/defaultType selectors
 * interactive. Task 4 flips it fully editable: every OIDC credential,
 * domain, scope, PingOne metadata, and the enabled toggle become
 * controlled inputs driven by the parent tab's state.
 *
 * The parent tab (`SsoConfigTab`) owns all state so the save handler
 * can build a single PATCH payload via `buildPatchPayload` with its
 * secret-safe + change-only guarantees.
 *
 * Secrets: the `clientSecret` input shows the masked literal "***" on
 * load. `buildPatchPayload` drops the masked value from the PATCH so
 * the server preserves the stored secret. If the operator clears the
 * field and types a new value, that value is sent.
 */
export interface SsoConfigFormState {
  name: string;
  provider: SsoProvider;
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
  scopesRaw: string;
  domainsRaw: string;
  pingOneRegion: string;
  pingOneEnvironmentId: string;
  isEnabled: boolean;
  emailClaim: string;
  rolesClaim: string;
  locationsClaim: string;
}

export interface SsoConfigFormProps {
  /** The stored config. `null` means no config exists for this company yet. */
  config: SsoConfig | null;
  /** Controlled form state — tab owns it so save can diff against `config`. */
  form: SsoConfigFormState;
  /** Partial update from a single field change; tab merges into the state. */
  onFormChange: (patch: Partial<SsoConfigFormState>) => void;
  /** Role-mapping state (Task 2). */
  roleRules: RoleToTypeRule[];
  onRoleRulesChange: (rules: RoleToTypeRule[]) => void;
  /** Policy + default-type selectors (Task 3 / SF-3593). */
  policy: ExistingUserRolePolicy;
  onPolicyChange: (p: ExistingUserRolePolicy) => void;
  locationPolicy: ExistingUserLocationPolicy;
  onLocationPolicyChange: (p: ExistingUserLocationPolicy) => void;
  defaultType: string;
  onDefaultTypeChange: (t: string) => void;
  /** Per-field validation messages (Task 4). Empty object = clean. */
  errors: FieldErrors;
  /** Disable every input (e.g. while a save is in-flight). */
  disabled?: boolean;
}

const PROVIDER_OPTIONS: { value: SsoProvider; label: string }[] = [
  { value: 'pingone', label: 'PingOne' },
  { value: 'pingfed', label: 'PingFederate' },
  { value: 'oidc', label: 'Generic OIDC' },
];

export const SsoConfigForm: React.FC<SsoConfigFormProps> = ({
  config,
  form,
  onFormChange,
  roleRules,
  onRoleRulesChange,
  policy,
  onPolicyChange,
  locationPolicy,
  onLocationPolicyChange,
  defaultType,
  onDefaultTypeChange,
  errors,
  disabled,
}) => {
  const secretHelp = config
    ? 'Stored encrypted. Leave as "***" to keep the saved secret; type a new value to rotate it.'
    : 'Stored encrypted by sf-auth-service. Required for a new config.';

  return (
    <Form layout="vertical" disabled={disabled}>
      {!config && (
        <Alert
          type="info"
          showIcon
          message="No SSO configuration exists for this company yet."
          description="Fill in the provider, OIDC credentials, and at least one email domain, then click Create SSO configuration to enable SSO for this company."
          style={{ marginBottom: 16 }}
        />
      )}

      <Typography.Title level={5} style={{ marginTop: 0 }}>
        Identity Provider
      </Typography.Title>

      <Form.Item
        label="Config Name"
        required
        validateStatus={errors.name ? 'error' : undefined}
        help={errors.name}
      >
        <Input
          value={form.name}
          onChange={(e) => onFormChange({ name: e.target.value })}
          placeholder="e.g. Acme PingOne"
          aria-label="Config name"
        />
      </Form.Item>

      <Form.Item
        label="Provider"
        help={
          config
            ? 'Provider is immutable after creation — delete this config and create a new one to switch providers.'
            : undefined
        }
      >
        <Select
          value={form.provider}
          onChange={(provider) => onFormChange({ provider })}
          options={PROVIDER_OPTIONS}
          aria-label="Provider"
          // SF-3404 blocker fix: lock provider on existing configs. Provider
          // drives discovery URL shape, attribute mapping defaults, and the
          // server's validation rules — silently switching it on an
          // already-saved config would mix incompatible fields.
          disabled={!!config}
        />
      </Form.Item>

      <Form.Item label="Enabled">
        <Switch
          checked={form.isEnabled}
          onChange={(isEnabled) => onFormChange({ isEnabled })}
          aria-label="SSO enabled"
        />
      </Form.Item>

      <Typography.Title level={5}>OIDC Credentials</Typography.Title>

      <Form.Item
        label="Client ID"
        required
        validateStatus={errors.clientId ? 'error' : undefined}
        help={errors.clientId}
      >
        <Input
          value={form.clientId}
          onChange={(e) => onFormChange({ clientId: e.target.value })}
          placeholder="OIDC client id"
          aria-label="Client ID"
        />
      </Form.Item>

      <Form.Item
        label="Client Secret"
        required={!config}
        validateStatus={errors.clientSecret ? 'error' : undefined}
        help={errors.clientSecret ?? secretHelp}
      >
        <Input.Password
          value={form.clientSecret}
          onChange={(e) => onFormChange({ clientSecret: e.target.value })}
          placeholder={config ? MASKED_SECRET : 'Enter new client secret'}
          aria-label="Client Secret"
          // Don't auto-select the masked literal — on focus we want the
          // operator to clear it themselves to enter a new value, so the
          // "didn't touch" path (preserve stored secret) is the default.
          autoComplete="new-password"
        />
      </Form.Item>

      <Form.Item
        label="Discovery URL"
        validateStatus={errors.discoveryUrl ? 'error' : undefined}
        help={
          errors.discoveryUrl ??
          'The OIDC .well-known/openid-configuration URL. Optional for PingOne when region + environment ID are provided.'
        }
      >
        <Input
          value={form.discoveryUrl}
          onChange={(e) => onFormChange({ discoveryUrl: e.target.value })}
          placeholder="https://…/.well-known/openid-configuration"
          aria-label="Discovery URL"
        />
      </Form.Item>

      <Form.Item
        label="Scopes"
        validateStatus={errors.scopes ? 'error' : undefined}
        help={errors.scopes ?? 'Comma-separated. Defaults: openid, profile, email.'}
      >
        <Input
          value={form.scopesRaw}
          onChange={(e) => onFormChange({ scopesRaw: e.target.value })}
          placeholder="openid, profile, email"
          aria-label="Scopes"
        />
      </Form.Item>

      <Typography.Title level={5}>Domains</Typography.Title>

      <Form.Item
        label="Email Domains"
        required
        validateStatus={errors.domains ? 'error' : undefined}
        help={
          errors.domains ??
          'Comma-separated list of email domains that should route to this IdP (e.g. acme.com, acme.co.uk).'
        }
      >
        <Input
          value={form.domainsRaw}
          onChange={(e) => onFormChange({ domainsRaw: e.target.value })}
          placeholder="acme.com, acme.co.uk"
          aria-label="Email Domains"
        />
      </Form.Item>

      {form.provider === 'pingone' && (
        <>
          <Typography.Title level={5}>PingOne</Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
            Supply Region + Environment ID instead of a discovery URL and the auth service will
            derive the URL at runtime.
          </Typography.Paragraph>
          <Space direction="horizontal" size="large" style={{ display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item
              label="Region"
              validateStatus={errors.pingOneRegion ? 'error' : undefined}
              help={errors.pingOneRegion}
            >
              <Input
                value={form.pingOneRegion}
                onChange={(e) => onFormChange({ pingOneRegion: e.target.value })}
                placeholder="eu"
                style={{ width: 160 }}
                aria-label="Region"
              />
            </Form.Item>
            <Form.Item
              label="Environment ID"
              validateStatus={errors.pingOneEnvironmentId ? 'error' : undefined}
              help={errors.pingOneEnvironmentId}
            >
              <Input
                value={form.pingOneEnvironmentId}
                onChange={(e) => onFormChange({ pingOneEnvironmentId: e.target.value })}
                placeholder="UUID"
                style={{ width: 320 }}
                aria-label="PingOne environment ID"
              />
            </Form.Item>
          </Space>
        </>
      )}

      <Typography.Title level={5}>Attribute Mapping (optional)</Typography.Title>

      <Form.Item label="Email claim">
        <Input
          value={form.emailClaim}
          onChange={(e) => onFormChange({ emailClaim: e.target.value })}
          placeholder="email"
          aria-label="Email claim"
        />
      </Form.Item>
      <Form.Item label="Roles claim">
        <Input
          value={form.rolesClaim}
          onChange={(e) => onFormChange({ rolesClaim: e.target.value })}
          placeholder="roles"
          aria-label="Roles claim"
        />
      </Form.Item>
      <Form.Item label="Locations claim">
        <Input
          value={form.locationsClaim}
          onChange={(e) => onFormChange({ locationsClaim: e.target.value })}
          placeholder="locations"
          aria-label="Locations claim"
        />
      </Form.Item>

      <Typography.Title level={5}>Role Mapping Rules</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
        Map role values from the IdP (claim configured above under
        <em> Roles claim</em>) to ServeFirst user types. The first matching rule wins; unmatched
        users fall back to the default user type below.
      </Typography.Paragraph>
      <RoleMappingRulesEditor value={roleRules} onChange={onRoleRulesChange} disabled={disabled} />

      <Typography.Title level={5}>Policy &amp; Default</Typography.Title>
      <PolicyAndDefaultSelectors
        policy={policy}
        locationPolicy={locationPolicy}
        defaultType={defaultType}
        onPolicyChange={onPolicyChange}
        onLocationPolicyChange={onLocationPolicyChange}
        onDefaultTypeChange={onDefaultTypeChange}
        disabled={disabled}
      />
    </Form>
  );
};
